import { IMPORT_TYPE } from 'data/types/import.types';
import get from 'lodash/get';

export const CUSTOM_PROPERTIES_ACTIONS_TYPE = Object.freeze({
  SINGLE: 'single',
  MULTIPLE: 'multiple',
});

const requiredMatchedProperties = {
  list_of_enrichment: {
    contact: {
      primary: [['firstName', 'lastName'], ['custom.properties.name'], ['linkedinUrl']],
      additional: [['companyRelations.company.name'], ['companyRelations.company.domain'], ['linkedinUrl']],
    },
    company: {
      primary: [['domain'], ['name']],
      additional: [],
    },
  },
  existing_list: {
    contact: {
      primary: [['firstName'], ['lastName'], ['custom.properties.name']],
      additional: [],
    },
    company: {
      primary: [['domain'], ['name']],
      additional: [],
    },
  },
  ignore_list: {
    contact: {
      primary: [['firstName', 'lastName']],
      additional: [],
    },
    company: {
      primary: [['domain']],
      additional: [],
    },
  },
  opt_out_list: {
    contact: {
      primary: [['email']],
      additional: [],
    },
  },
};

const requiredMatchedLabels = {
  list_of_enrichment: {
    contact:
      'Matching must contain - First name + Last name or Full name and must contain some of this fields - Company name, Company domain, LinkedIn',
    company: 'Matching must contain some of this fields - Name, Company domain',
  },
  existing_list: {
    contact: 'Matching must contain some of this fields - First name, Last name, Full name',
    company: 'Matching must contain some of this fields - Name, Company domain',
  },
  ignore_list: {
    contact: 'Matching must contain First Name + Last Name',
    company: 'Matching must contain Domain',
  },
  opt_out_list: {
    contact: 'Matching must contain Email',
  },
};

const customProperties = [
  {
    name: 'companyRelations.email.value',
    label: 'Corporate Email',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    customSystem: true,
    autoMatchedColumn: /^email(\s\d+)?$/i,
  },
  {
    name: 'companyRelations.email.status',
    label: 'Corporate Email status',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    autoMatchedColumn: /^email status(\s\d+)?$/i,
  },
  {
    name: 'companyRelations.position',
    label: 'Job title',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    autoMatchedColumn: /^position(\s\d+)?$/i,
  },
  {
    name: 'companyRelations.company.domain',
    label: 'Company Domain',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    autoMatchedColumn: /^company domain(\s\d+)?$/i,
  },
  {
    name: 'companyRelations.company.name',
    label: 'Company Name',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    autoMatchedColumn: /^company name(\s\d+)?$/i,
  },
  {
    name: 'companyRelations.company.size',
    label: 'Company Size',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    autoMatchedColumn: /^company size(\s\d+)?$/i,
  },
  {
    name: 'companyRelations.company.description',
    label: 'Company Description',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    autoMatchedColumn: /^company description(\s\d+)?$/i,
  },
  {
    name: 'companyRelations.company.industry',
    label: 'Company Industry',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    autoMatchedColumn: /^company industry(\s\d+)?$/i,
  },
  {
    name: 'companyRelations.company.headquarters',
    label: 'Company Headquarters',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    autoMatchedColumn: /^company headquarters(\s\d+)?$/i,
  },
  {
    name: 'companyRelations.company.postalCode',
    label: 'Company Postal Code',
    tooltipTitle: 'This fields used for contact-company relation',
    type: ['company', 'contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
    autoMatch: true,
    autoMatchedColumn: /^company postal code(\s\d+)?$/i,
  },
  {
    name: 'custom.properties.name',
    label: 'Full name',
    tooltipTitle: 'This field will be split into First name and Last name fields',
    type: ['contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.SINGLE,
    autoMatch: true,
    autoMatchedColumn: 'name',
    customMatching: [
      (itemData) => {
        const { previewInformation = [] } = itemData;
        return previewInformation.every((data) => data && data?.includes(' '));
      },
    ],
  },
  {
    name: 'getProspectId',
    label: 'GetProspect ID',
    tooltipTitle: 'This field used for getprospect id',
    type: ['contact'],
    action: CUSTOM_PROPERTIES_ACTIONS_TYPE.SINGLE,
    autoMatch: true,
    autoMatchedColumn: 'getProspectId',
    renderIf: (importSettings) => get(importSettings, 'userContactId', false),
  },
];

export const getCustomPropertiesByType = (importSettings) => {
  if (get(importSettings, 'type') === IMPORT_TYPE.OPT_OUT_LIST) {
    return [
      {
        name: 'email',
        label: 'Email',
        tooltipTitle: '',
        type: ['contact'],
        action: CUSTOM_PROPERTIES_ACTIONS_TYPE.MULTIPLE,
        autoMatch: true,
        customSystem: true,
        autoMatchedColumn: /^email(\s\d+)?$/i,
      },
    ];
  }
  return customProperties.filter(
    (customProperty) =>
      customProperty.type.includes(importSettings.source) &&
      (!customProperty.renderIf || customProperty.renderIf(importSettings)),
  );
};
export const getRequiredMatchingByType = (importType, sourceType) => requiredMatchedProperties[importType][sourceType];
export const getRequiredMatchingLabel = (importType, sourceType) => requiredMatchedLabels[importType][sourceType];
